/*----------------------------------
- HOOK: HOME SCREENER BLUEPRINT VIDEO
----------------------------------*/

import React from 'react';

import useReducedMotionPreference from '@/client/hooks/useReducedMotionPreference';
import {
    HOME_SCREENER_BLUEPRINT,
    type HomeScreenerBlueprintVariant,
} from '@/common/catalogs/landing/homeScreenerBlueprint';
import { appErrorReporter } from '@/common/libs/errorReporting';
import {
    getHomeScreenerBlueprintPlaybackState,
    shouldRequestHomeScreenerBlueprintLoad,
} from './homeScreenerBlueprintPlayback';

/*----------------------------------
- TYPES
----------------------------------*/

type NetworkInformation = EventTarget & {
    readonly saveData?: boolean;
};

type NavigatorWithConnection = Navigator & {
    readonly connection?: NetworkInformation;
};

type HomeScreenerBlueprintVideoOptions = {
    /** Hold the automatic load until the visitor first scrolls, points, types or touches. */
    deferUntilInteraction?: boolean;
};

type HomeScreenerBlueprintVideo = {
    figureRef: React.RefObject<HTMLElement>;
    isPlaying: boolean;
    mediaFailed: boolean;
    onCanPlay: () => void;
    onError: () => void;
    onPause: () => void;
    onPlay: () => void;
    showVideo: boolean;
    togglePlayback: () => void;
    variant: HomeScreenerBlueprintVariant;
    videoRef: React.RefObject<HTMLVideoElement>;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const LOAD_ROOT_MARGIN = '240px 0px';
const PLAYBACK_THRESHOLD = 0.12;
/** The first of these ends the wait `deferUntilInteraction` imposes. */
const INTERACTION_EVENTS = ['scroll', 'pointerdown', 'keydown', 'touchstart'] as const;

/*----------------------------------
- HELPERS
----------------------------------*/

const getConnection = (): NetworkInformation | undefined => {
    if (typeof navigator === 'undefined') return undefined;
    return (navigator as NavigatorWithConnection).connection;
};

const getSaveDataPreference = (): boolean => getConnection()?.saveData === true;

const getMobileMediaQueryList = (): MediaQueryList | undefined => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    return window.matchMedia(HOME_SCREENER_BLUEPRINT.mobileMediaQuery);
};

// The first client render must agree with SSR, which has no viewport and emits
// the landscape cut. Resolving the real variant during that render instead would
// silently lose it: Preact applies only event-handler props while hydrating, so
// the swapped `<source>` and dimension attributes would never reach the DOM, and
// no later re-render would repair them, because the vnode already claims them.
// The variant therefore settles in an effect, one render after hydration.
const INITIAL_VARIANT: HomeScreenerBlueprintVariant = 'desktop';

/*----------------------------------
- HOOK
----------------------------------*/

const useHomeScreenerBlueprintVideo = ({
    deferUntilInteraction = false,
}: HomeScreenerBlueprintVideoOptions = {}): HomeScreenerBlueprintVideo => {
    const figureRef = React.useRef<HTMLElement>(null);
    const videoRef = React.useRef<HTMLVideoElement>(null);
    // Which cut the element has been told to load, not whether a load happened.
    // Swapping `<source>` children never re-runs resource selection on its own,
    // so a variant change has to be able to request a fresh load exactly once.
    const loadedVariantRef = React.useRef<HomeScreenerBlueprintVariant | null>(null);
    const prefersReducedMotion = useReducedMotionPreference();
    const [documentVisible, setDocumentVisible] = React.useState(
        () => typeof document === 'undefined' || document.visibilityState === 'visible',
    );
    const [explicitlyRequested, setExplicitlyRequested] = React.useState(false);
    const [hasEnteredLoadMargin, setHasEnteredLoadMargin] = React.useState(false);
    const [hasInteracted, setHasInteracted] = React.useState(false);
    const [inViewport, setInViewport] = React.useState(false);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [pausedByUser, setPausedByUser] = React.useState(false);
    const [playbackBlocked, setPlaybackBlocked] = React.useState(false);
    const [savesData, setSavesData] = React.useState(getSaveDataPreference);
    const [hasPlayed, setHasPlayed] = React.useState(false);
    const [mediaFailed, setMediaFailed] = React.useState(false);
    const [videoReady, setVideoReady] = React.useState(false);
    const [variant, setVariant] = React.useState<HomeScreenerBlueprintVariant>(INITIAL_VARIANT);
    const [variantResolved, setVariantResolved] = React.useState(false);
    const playback = getHomeScreenerBlueprintPlaybackState({
        documentVisible,
        explicitlyRequested,
        hasEnteredLoadMargin,
        hasInteracted,
        inViewport,
        interactionRequired: deferUntilInteraction,
        pausedByUser,
        playbackBlocked,
        prefersReducedMotion,
        savesData,
    });

    const onCanPlay = React.useCallback(() => {
        setMediaFailed(false);
        setVideoReady(true);
    }, []);

    const onError = React.useCallback(() => {
        const error = videoRef.current?.error ?? new Error('The homepage screener motion could not be loaded.');
        appErrorReporter.app.handleError(error);
        setIsPlaying(false);
        setMediaFailed(true);
        setPlaybackBlocked(true);
        setVideoReady(false);
    }, []);

    const onPause = React.useCallback(() => {
        setIsPlaying(false);
    }, []);

    const onPlay = React.useCallback(() => {
        setHasPlayed(true);
        setIsPlaying(true);
    }, []);

    const togglePlayback = React.useCallback(() => {
        const video = videoRef.current;

        if (isPlaying) {
            setPausedByUser(true);
            video?.pause();
            return;
        }

        setExplicitlyRequested(true);
        setHasEnteredLoadMargin(true);
        setPausedByUser(false);
        setPlaybackBlocked(false);
    }, [isPlaying]);

    React.useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const syncVisibility = () => setDocumentVisible(document.visibilityState === 'visible');
        syncVisibility();
        document.addEventListener('visibilitychange', syncVisibility);
        return () => document.removeEventListener('visibilitychange', syncVisibility);
    }, []);

    React.useEffect(() => {
        if (!deferUntilInteraction || typeof window === 'undefined') return undefined;

        // One-shot and passive: the first interaction flips the flag, then every listener goes,
        // so the wait costs neither scroll performance nor a listener for the rest of the visit.
        const markInteracted = () => {
            setHasInteracted(true);
            for (const eventName of INTERACTION_EVENTS) window.removeEventListener(eventName, markInteracted);
        };
        for (const eventName of INTERACTION_EVENTS) {
            window.addEventListener(eventName, markInteracted, { once: true, passive: true });
        }
        return () => {
            for (const eventName of INTERACTION_EVENTS) window.removeEventListener(eventName, markInteracted);
        };
    }, [deferUntilInteraction]);

    React.useEffect(() => {
        const connection = getConnection();
        if (!connection) return undefined;

        const syncSaveData = () => setSavesData(connection.saveData === true);
        syncSaveData();
        connection.addEventListener('change', syncSaveData);
        return () => connection.removeEventListener('change', syncSaveData);
    }, []);

    React.useEffect(() => {
        const mediaQueryList = getMobileMediaQueryList();
        // Without matchMedia the landscape cut is the resolved answer, not a
        // pending one, so loading must not stay blocked waiting for a viewport.
        if (!mediaQueryList) {
            setVariantResolved(true);
            return undefined;
        }

        const syncVariant = () => {
            setVariant(mediaQueryList.matches ? 'mobile' : 'desktop');
            setVariantResolved(true);
        };
        syncVariant();
        mediaQueryList.addEventListener('change', syncVariant);
        return () => mediaQueryList.removeEventListener('change', syncVariant);
    }, []);

    React.useEffect(() => {
        if (!prefersReducedMotion && !savesData) return;
        setExplicitlyRequested(false);
    }, [prefersReducedMotion, savesData]);

    React.useEffect(() => {
        const figure = figureRef.current;
        if (!figure || typeof window === 'undefined') return undefined;

        if (!('IntersectionObserver' in window)) {
            setHasEnteredLoadMargin(true);
            setInViewport(true);
            return undefined;
        }

        const loadObserver = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                setHasEnteredLoadMargin(true);
                loadObserver.disconnect();
            },
            { rootMargin: LOAD_ROOT_MARGIN, threshold: 0 },
        );
        const playbackObserver = new IntersectionObserver(
            (entries) => {
                setInViewport(
                    entries.some(
                        (entry) => entry.isIntersecting && entry.intersectionRatio >= PLAYBACK_THRESHOLD,
                    ),
                );
            },
            { rootMargin: '0px', threshold: [0, PLAYBACK_THRESHOLD] },
        );

        loadObserver.observe(figure);
        playbackObserver.observe(figure);
        return () => {
            loadObserver.disconnect();
            playbackObserver.disconnect();
        };
    }, []);

    // Crossing the breakpoint, which is what rotating a phone does, returns the
    // figure to its poster for the incoming cut. `video.load()` below rewinds
    // the element anyway, so showing the outgoing cut's last frame over portrait
    // geometry would only be a mismatched flash. A manual pause is deliberately
    // not reset here: it stays authoritative for the rest of the page visit.
    React.useEffect(() => {
        setIsPlaying(false);
        setMediaFailed(false);
        setPlaybackBlocked(false);
        setHasPlayed(false);
        setVideoReady(false);
    }, [variant]);

    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const requestsLoad = shouldRequestHomeScreenerBlueprintLoad({
            loadedVariant: loadedVariantRef.current,
            shouldLoad: playback.shouldLoad,
            variant,
            variantResolved,
        });
        if (!requestsLoad) return;

        loadedVariantRef.current = variant;
        video.load();
    }, [playback.shouldLoad, variant, variantResolved]);

    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return undefined;

        if (!playback.shouldPlay) {
            if (isPlaying) video.pause();
            return undefined;
        }

        if (isPlaying) return undefined;

        let disposed = false;
        const playRequest = video.play();
        if (playRequest) {
            void playRequest.catch((error) => {
                appErrorReporter.app.handleError(error);
                if (disposed) return;
                setIsPlaying(false);
                setPlaybackBlocked(true);
            });
        }

        return () => {
            disposed = true;
        };
    }, [isPlaying, playback.shouldPlay]);

    return {
        figureRef,
        isPlaying,
        mediaFailed,
        onCanPlay,
        onError,
        onPause,
        onPlay,
        showVideo: videoReady && hasPlayed && !mediaFailed,
        togglePlayback,
        variant,
        videoRef,
    };
};

export default useHomeScreenerBlueprintVideo;
